import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';

import { useAuth } from '@/auth/useAuth';
import { useModules } from '@/modules/ModuleContext';
import { Button } from '@/ui/Button';
import { Icon } from '@/ui/Icon';
import { offeredNewEntries } from './newEntries';

/** The working surface, where focus goes once something has been opened from the menu. */
const MAIN_ID = 'main';

type Start = 'first' | 'last';

function menuItems(root: HTMLElement | null): HTMLElement[] {
  return Array.from(root?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
}

/**
 * The "New" button and the menu it opens: start a job, a lead, a customer or an invoice from
 * anywhere.
 *
 * It is a menu button in the ARIA sense. Enter, Space and the Down arrow open it on the first
 * entry, the Up arrow on the last; the arrows, Home and End move between entries; Escape closes it
 * and puts focus back on the button, and so does Tab, so that the next Tab goes on from where the
 * button is. Each entry is a link, so it can be opened in a new tab.
 *
 * An entry is listed only to a person who holds what its target needs, and the menu is not drawn
 * at all when there is nothing to list.
 *
 * @returns the button and, while open, its menu; nothing when there is nothing to start
 */
export function NewMenu(): React.JSX.Element | null {
  const { can } = useAuth();
  const enabled = useModules();
  const entries = useMemo(() => offeredNewEntries({ can, enabled }), [can, enabled]);
  const [open, setOpen] = useState(false);
  const start = useRef<Start>('first');
  const root = useRef<HTMLDivElement>(null);
  const buttonId = useId();
  const listId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    const items = menuItems(root.current);
    (start.current === 'last' ? items.at(-1) : items[0])?.focus();
  }, [open]);

  if (entries.length === 0) {
    return null;
  }

  function openAt(where: Start): void {
    start.current = where;
    setOpen(true);
  }

  function closeAndReturn(): void {
    setOpen(false);
    document.getElementById(buttonId)?.focus();
  }

  function handleButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(event.key === 'ArrowUp' ? 'last' : 'first');
    }
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const items = menuItems(root.current);
    const index = items.findIndex((item) => item === document.activeElement);
    const next = index + 1 >= items.length ? 0 : index + 1;
    const previous = index <= 0 ? items.length - 1 : index - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[next]?.focus();
        return;
      case 'ArrowUp':
        event.preventDefault();
        items[previous]?.focus();
        return;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        return;
      case 'End':
        event.preventDefault();
        items.at(-1)?.focus();
        return;
      case 'Escape':
      case 'Tab':
        event.preventDefault();
        event.stopPropagation();
        closeAndReturn();
        return;
      default:
    }
  }

  return (
    <div
      ref={root}
      className="fo-newmenu"
      onBlur={(event) => {
        if (open && !event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <Button
        id={buttonId}
        variant="secondary"
        size="sm"
        icon="plus"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => {
          if (open) {
            closeAndReturn();
          } else {
            openAt('first');
          }
        }}
        onKeyDown={handleButtonKeyDown}
      >
        New
      </Button>
      {open && (
        <div
          id={listId}
          className="fo-newmenu__list"
          role="menu"
          aria-labelledby={buttonId}
          onKeyDown={handleMenuKeyDown}
        >
          {entries.map((entry) => (
            <Link
              key={entry.id}
              to={entry.to}
              className="fo-newmenu__item"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                setOpen(false);
                document.getElementById(MAIN_ID)?.focus();
              }}
            >
              <Icon name={entry.icon} />
              {entry.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
